import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import BlockIcon from '@mui/icons-material/Block';
import {
  Box,
  TextField,
  Grid,
  IconButton,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CommonDataGrid from '../Stateless/CommonDataGrid';
import { GetItemSubCategoriesResponse } from '../../client-sdk/api';
import {
  useAllItemSubCategory,
  useDeleteItemSubCategory,
  usePatchItemSubCategory,
} from '../Query/ItemSubCategoryQuery';
import { categoryStyles } from '../Styles/CategoryFormStyles';
import { useManageCmsParams } from './ManageCMS/useManageCmsParams';
import { useParams, useNavigate } from 'react-router-dom';
import { useDebounce } from './Utils/useDounce';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { usePermissions } from '../../context/PermissionContext';
import { Add } from '@mui/icons-material';
import PermissionActionButtons from '../Stateless/Common/PermissionActionButtons';

type StoreWithSno = {
  id: string;
  sno: number;
  serialNumber: number;
  name: string;
  addedBy: any;
  status: any;
  [key: string]: any;
};

// Define ItemSubCategory type based on the API response
type ItemSubCategory = GetItemSubCategoriesResponse;

interface ItemSubCategoryListProps {
  editCategory: (category: any) => void;
  handleAddCategory: () => void;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  mode: 'add' | 'edit';
  setMode: React.Dispatch<React.SetStateAction<'add' | 'edit'>>;
  storeId?: string;
}

const ItemSubCategoryList: React.FC<ItemSubCategoryListProps> = ({
  editCategory,
  handleAddCategory,
  setMode,
}) => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const { params, dispatch } = useManageCmsParams();
  const { categoryId } = useParams<{ categoryId: string }>();
  const isReady = Boolean(params && Object.keys(params).length > 0);

  const memoizedParams = useMemo(() => {
    if (!isReady || !categoryId) return {};
    return {
      ...params,
      categoryId,
    };
  }, [isReady, params, categoryId]);

  const { data: manageItemSubCategory, isLoading } = useAllItemSubCategory(memoizedParams)

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { mutate: patchItemSubCategory } = usePatchItemSubCategory();

  const { mutate: deleteItemSubCategory } = useDeleteItemSubCategory();

  const filteredRows = React.useMemo(() => {
    const storeOwners = manageItemSubCategory?.data || [];
    return storeOwners.map((row, index) => {
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

      const rowWithSno: StoreWithSno = {
        ...row,
        id: row.id,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: row.nameEnglish,
        addedBy: row.addedBy,
        status: row.status,
      };

      return rowWithSno;
    });
  }, [params.search, manageItemSubCategory, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleEditCategory = (category: ItemSubCategory) => {
    editCategory(category);
    setMode('edit');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one sub-category to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchItemSubCategory(
        {
          id: selectedRows,
          data: { status: 'blocked' },
        },
        {
          onSuccess: () => {
            setSelectedRows([]);
            showMessage('Sub-categories blocked successfully', 'success');
          },
          onError: () => {
            showMessage('Error blocking sub-categories', 'error');
          },
        }
      );
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one sub-category to unblock', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchItemSubCategory(
        {
          id: selectedRows,
          data: { status: 'unblocked' },
        },
        {
          onSuccess: () => {
            setSelectedRows([]);
            showMessage('Sub-categories unblocked successfully', 'success');
          },
          onError: () => {
            showMessage('Error unblocking sub-categories', 'error');
          },
        }
      );
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Select at least one sub-category', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      selectedRows.forEach((id) => {
        deleteItemSubCategory(id, {
          onSuccess: () => {
            setSelectedRows([]);
            showMessage('Sub-category deleted successfully', 'success');
          },
          onError: () => {
            showMessage('Error deleting sub-category', 'error');
          },
        });
      });
    }
  }, [selectedRows, deleteItemSubCategory, showMessage]);

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteItemSubCategory(id, {
        onSuccess: () => {
          setSelectedRows([]);
          showMessage('Sub-category deleted successfully', 'success');
        },
        onError: () => {
          showMessage('Error deleting sub-category', 'error');
        },
      });
    },
    [deleteItemSubCategory, showMessage]
  );

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this sub-category?'
    );
    if (!confirmed) return;

    await patchItemSubCategory(
      {
        id: [id],
        data: { status: 'blocked' },
      },
      {
        onSuccess: () => {
          setSelectedRows([]);
          showMessage('Sub-category blocked successfully', 'success');
        },
        onError: () => {
          showMessage('Error blocking sub-category', 'error');
        },
      }
    );
  };

  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this sub-category?'
    );
    if (!confirmed) return;

    await patchItemSubCategory(
      {
        id: [id],
        data: { status: 'unblocked' },
      },
      {
        onSuccess: () => {
          setSelectedRows([]);
          showMessage('Sub-category unblocked successfully', 'success');
        },
        onError: () => {
          showMessage('Error unblocking sub-category', 'error');
        },
      }
    );
  };

  // Determine which bulk buttons to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = manageItemSubCategory?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.id), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return { showBlock: hasUnblocked, showUnblock: hasBlocked };
  }, [selectedRows, manageItemSubCategory]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('itemSubCategory', 'Edit');
    const canDelete = hasPermission('itemSubCategory', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(row); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDeleteButton(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = [
    ...(hasPermission('itemSubCategory', 'Block') ||
    hasPermission('itemSubCategory', 'Unblock')
      ? [
          {
            field: 'status',
            headerName: 'Status',
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params: any) => {
              const isBlocked = params.value === 'blocked';
              const id = params.row.id;
              const validId = typeof id === 'string' ? id : '';
              const action = isBlocked ? 'Unblock' : 'Block';

              if (!hasPermission('itemSubCategory', action)) {
                return null;
              }

              return (
                <IconButton
                  size="small"
                  sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
                  onClick={() =>
                    isBlocked
                      ? handleUnBlockClickButton(validId)
                      : handleBlockClickButton(validId)
                  }
                >
                  {isBlocked ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleIcon fontSize="small" />
                  )}
                </IconButton>
              );
            },
          },
        ]
      : []),
    {
      field: 'serialNumber',
      headerName: 'S.No',
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: 'name',
      headerName: 'Category Name',
      flex: 1,
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
    // Delete moved to actions menu
  ];

  return (
    <>
      <Box sx={categoryStyles.ListContainer}>
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          wrap="wrap"
          sx={{ mb: 2 }}
        >
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              size="medium"
              placeholder="Search by Item Sub Category name"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: '#aaa' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Grid
            container
            justifyContent="flex-end"
            spacing={1}
            alignItems="center"
          >
            <Grid>
              <Button
                variant="contained"
                size="small"
                onClick={handleBackClick}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                startIcon={<ArrowBackIcon sx={{ fontSize: 16, color: '#fff' }} />}
              >
                Back
              </Button>
            </Grid>
            <Grid>
              <PermissionActionButtons
                module="itemSubCategory"
                onBlock={showBlock ? handleBlockClick : undefined}
                onUnblock={showUnblock ? handleUnBlockClick : undefined}
                onDelete={selectedRows.length ? handleDeleteClick : undefined}
              />
            </Grid>
            <Grid>
              {hasPermission('itemSubCategory', 'Add') && (
                <Button
                  onClick={handleAddCategory}
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                  startIcon={<Add sx={{ fontSize: 16, color: '#fff' }} />}
                >
                  Add Item Sub Category
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Box width="100%" sx={{ overflowX: 'auto' }}>
          <CommonDataGrid
            key={`datagrid-page-${params.page}-limit-${params.limit}`}
            rows={filteredRows}
            columns={columns}
            pageSize={params.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            checkboxSelection
            selectionModel={selectedRows}
            onRowSelectionModelChange={setSelectedRows}
            loading={isLoading}
            components={{
              LoadingOverlay: () => null,
              NoRowsOverlay: () => (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  No sub-categories found
                </Box>
              ),
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default ItemSubCategoryList;
